import {analyticsLogger} from 'analytics/analyticsLogger';
import type {LiveAnswerPlacement} from 'analytics/liveAnswerAnalytics';
import {AI_DRAWER_MIN_WIDTH_PX} from 'atoms/aiSearch';
import type {QueryShape} from 'helpers/queryShape';

export type AiSearchSurface = 'ai_search';

export type AiSearchAskSource =
  | 'home'
  | 'nav'
  | 'people'
  | 'keyboard'
  | 'queryparam'
  | 'mcp_setup'
  | 'drawer_composer'
  | 'search_typeahead_row'
  | 'search_keyboard'
  | 'search_suggestion'
  | 'search_instant_answer'
  // The search box's own Ask entry points: a meeting's asks, a person, team or
  // workstream pane's asks, your Up next card, and Continue in Ask under a
  // live answer.
  | 'search_meeting'
  | 'search_preview'
  | 'search_up_next'
  | 'search_live_answer'
  | 'home_knowledge_chip'
  | 'search_knowledge_chip'
  | 'profile';

export type AiSearchSuggestionSource = 'search' | 'drawer' | 'profile';

export type AiSearchDrawerOpenSource = 'search_ask' | 'nav_chip' | 'restore';

export type AiSearchInteractionAction =
  | 'ask'
  | 'tool_call'
  | 'turn_complete'
  | 'session_summary'
  | 'suggestion_click'
  | 'drawer_open'
  | 'drawer_minimize'
  | 'new_chat'
  | 'nav_chip_click'
  | 'resize'
  | 'step_expand'
  | 'table_link_click'
  | 'csv_download'
  | 'stop'
  | 'queue_message'
  | 'attach_interest'
  | 'answer_read';

export type AiSearchWidthBucket = 'sm' | 'md' | 'lg';

/**
 * Why a search pane answer was there: the pane led with Ask on its own while
 * the person typed, or they picked its row.
 */
export type AiSearchAnswerStartedBy = 'typing' | 'click';

export type AiSearchTableEntityType = 'person' | 'team' | 'workstream' | 'service' | 'component';

export type AiSearchTableLinkDestination =
  | 'profile'
  | 'team'
  | 'workstream'
  | 'internal'
  | 'external';

export type AiSearchCsvScope = 'step' | 'answer';

export type AiSearchAttachMethod = 'button' | 'drop' | 'paste';

export type AiSearchAttachFileKind =
  | 'image'
  | 'pdf'
  | 'spreadsheet'
  | 'document'
  | 'presentation'
  | 'text'
  | 'other';

const ATTACH_KIND_BY_EXTENSION: Record<string, AiSearchAttachFileKind> = {
  pdf: 'pdf',
  csv: 'spreadsheet',
  tsv: 'spreadsheet',
  xls: 'spreadsheet',
  xlsx: 'spreadsheet',
  numbers: 'spreadsheet',
  doc: 'document',
  docx: 'document',
  pages: 'document',
  rtf: 'document',
  ppt: 'presentation',
  pptx: 'presentation',
  key: 'presentation',
  txt: 'text',
  md: 'text',
  markdown: 'text',
  json: 'text',
};

/** Coarse file kind for attach-interest analytics; never the name itself. */
export const attachFileKind = (file: File): AiSearchAttachFileKind => {
  if (file.type.startsWith('image/')) {
    return 'image';
  }
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  return ATTACH_KIND_BY_EXTENSION[extension] ?? 'other';
};

type SessionFlags = {
  hadTableClick: boolean;
  hadCsvDownload: boolean;
  hadStepExpand: boolean;
};

type SessionState = {
  sessionId: string;
  interactionIndex: number;
  askCount: number;
  sessionStartedAt: number;
  flags: SessionFlags;
};

let session: SessionState | null = null;

const newSessionId = (): string => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `ai-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const createSession = (): SessionState => ({
  sessionId: newSessionId(),
  interactionIndex: 0,
  askCount: 0,
  sessionStartedAt: Date.now(),
  flags: {
    hadTableClick: false,
    hadCsvDownload: false,
    hadStepExpand: false,
  },
});

const ensureSession = (): SessionState => {
  if (!session) {
    session = createSession();
  }
  return session;
};

/** Mint a new client session (new chat or fresh search ask). */
export const resetAiSearchSession = () => {
  session = createSession();
  return session.sessionId;
};

const bumpSessionForAction = (action: AiSearchInteractionAction): SessionState => {
  const current = ensureSession();
  current.interactionIndex += 1;
  if (action === 'ask') {
    current.askCount += 1;
  }
  if (action === 'table_link_click') {
    current.flags.hadTableClick = true;
  }
  if (action === 'csv_download') {
    current.flags.hadCsvDownload = true;
  }
  if (action === 'step_expand') {
    current.flags.hadStepExpand = true;
  }
  return current;
};

export type AiSearchInteractionBase = {
  action: AiSearchInteractionAction;
};

export type AiSearchInteractionProps =
  | (AiSearchInteractionBase & {
      action: 'ask';
      source: AiSearchAskSource;
      turnIndex: number;
      queryLength: number;
      queryShape: QueryShape;
      conversationId?: string;
      // Search started the turn while the question was typed, and the ask took
      // it over; `speculativeLeadMs` is how long it had run by then (time saved).
      speculative?: boolean;
      speculativeLeadMs?: number;
    })
  | (AiSearchInteractionBase & {
      action: 'tool_call';
      toolName: string;
      turnIndex: number;
      conversationId?: string;
    })
  | (AiSearchInteractionBase & {
      // Emitted once per completed turn. `fullyAnswered` is the headline metric
      // (track the share of turns/conversations that got a full answer over
      // time); `answerGapStatus`/`answerGapScope`/`answerGapMissing` break down
      // the misses. `answerGapScope` separates addressable (buildable) gaps —
      // the number to drive down — from out-of-scope / not-permitted misses
      // that shouldn't count against answer-completeness improvements.
      action: 'turn_complete';
      turnIndex: number;
      conversationId?: string;
      fullyAnswered: boolean;
      // Wall time from ask to final answer, for speed charts.
      durationMs?: number;
      answerGapStatus?: 'partial' | 'unanswerable';
      answerGapScope?: 'addressable' | 'out_of_scope' | 'not_permitted';
      answerGapMissing?: string;
    })
  | (AiSearchInteractionBase & {
      action: 'session_summary';
      reason: 'new_chat' | 'minimize';
    })
  | (AiSearchInteractionBase & {
      action: 'suggestion_click';
      source: AiSearchSuggestionSource;
      suggestionIndex: number;
      // What the suggestion was about (workstream, policy, …), never its text.
      suggestionKind?: string;
    })
  | (AiSearchInteractionBase & {
      action: 'drawer_open';
      source: AiSearchDrawerOpenSource;
    })
  | (AiSearchInteractionBase & {action: 'drawer_minimize'})
  | (AiSearchInteractionBase & {action: 'new_chat'; turnCount: number})
  | (AiSearchInteractionBase & {action: 'nav_chip_click'})
  | (AiSearchInteractionBase & {action: 'resize'; widthBucket: AiSearchWidthBucket})
  | (AiSearchInteractionBase & {
      action: 'step_expand';
      toolName: string;
      expanded: boolean;
    })
  | (AiSearchInteractionBase & {
      action: 'table_link_click';
      entityType: AiSearchTableEntityType;
      destination: AiSearchTableLinkDestination;
      toolName?: string;
      columnKind?: string;
    })
  | (AiSearchInteractionBase & {
      action: 'csv_download';
      scope: AiSearchCsvScope;
      toolName?: string;
      rowCount: number;
    })
  | (AiSearchInteractionBase & {action: 'stop'; turnIndex: number})
  | (AiSearchInteractionBase & {
      action: 'queue_message';
      operation: 'queue' | 'delete' | 'edit' | 'send_now' | 'auto_send';
    })
  | (AiSearchInteractionBase & {
      // Fake door: attaching files isn't built yet, so this counts demand.
      // fileKind is the first file's kind; button clicks carry no file.
      action: 'attach_interest';
      method: AiSearchAttachMethod;
      fileKind?: AiSearchAttachFileKind;
      fileCount: number;
    })
  | (AiSearchInteractionBase & {
      // An Ask answer read in the search box without being sent: on screen
      // 3s after it finished, or one of its links clicked. Its own action so
      // `ask` charts read as before; usage counts `ask` OR `answer_read`.
      action: 'answer_read';
      source: 'search_live_answer';
      placement: LiveAnswerPlacement;
      cached: boolean;
      trigger: 'visible' | 'link';
      startedBy: AiSearchAnswerStartedBy;
      queryShape: QueryShape;
      // From shown to answered; none when a link was clicked before the answer finished.
      answerMs?: number;
      visibleMs: number;
    });

/** Bucket drawer width for analytics (min 400px). */
export const bucketDrawerWidth = (widthPx: number, maxWidthPx: number): AiSearchWidthBucket => {
  const range = Math.max(1, maxWidthPx - AI_DRAWER_MIN_WIDTH_PX);
  const third = range / 3;
  const offset = widthPx - AI_DRAWER_MIN_WIDTH_PX;
  if (offset < third) {
    return 'sm';
  }
  if (offset < third * 2) {
    return 'md';
  }
  return 'lg';
};

/** Infer link destination for table entity clicks. */
export const aiSearchLinkDestination = (path: string): AiSearchTableLinkDestination => {
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return 'external';
  }
  if (path.includes('/people/')) {
    return 'profile';
  }
  if (path.includes('/teams/')) {
    return 'team';
  }
  if (path.includes('/workstreams/')) {
    return 'workstream';
  }
  return 'internal';
};

const buildEventPayload = (
  current: SessionState,
  props: AiSearchInteractionProps
): Record<string, boolean | number | string | undefined> => {
  const base: Record<string, boolean | number | string | undefined> = {
    surface: 'ai_search',
    sessionId: current.sessionId,
    sessionInteractionIndex: current.interactionIndex,
    askCount: current.askCount,
    action: props.action,
  };

  switch (props.action) {
    case 'ask':
      return {
        ...base,
        source: props.source,
        turnIndex: props.turnIndex,
        isFollowUp: props.turnIndex > 0,
        queryLength: props.queryLength,
        queryShape: props.queryShape,
        conversationId: props.conversationId,
        speculative: props.speculative ?? false,
        speculativeLeadMs: props.speculativeLeadMs,
      };
    case 'tool_call':
      return {
        ...base,
        toolName: props.toolName,
        turnIndex: props.turnIndex,
        conversationId: props.conversationId,
      };
    case 'turn_complete':
      return {
        ...base,
        turnIndex: props.turnIndex,
        conversationId: props.conversationId,
        fullyAnswered: props.fullyAnswered,
        durationMs: props.durationMs,
        answerGapStatus: props.answerGapStatus,
        answerGapScope: props.answerGapScope,
        answerGapMissing: props.answerGapMissing,
      };
    case 'session_summary':
      return {
        ...base,
        reason: props.reason,
        interactionCount: current.interactionIndex,
        hadTableClick: current.flags.hadTableClick,
        hadCsvDownload: current.flags.hadCsvDownload,
        hadStepExpand: current.flags.hadStepExpand,
        durationMs: Date.now() - current.sessionStartedAt,
      };
    case 'suggestion_click':
      return {
        ...base,
        source: props.source,
        suggestionIndex: props.suggestionIndex,
        suggestionKind: props.suggestionKind,
      };
    case 'drawer_open':
      return {
        ...base,
        source: props.source,
      };
    case 'new_chat':
      return {
        ...base,
        turnCount: props.turnCount,
      };
    case 'resize':
      return {
        ...base,
        widthBucket: props.widthBucket,
      };
    case 'step_expand':
      return {
        ...base,
        toolName: props.toolName,
        expanded: props.expanded,
      };
    case 'table_link_click':
      return {
        ...base,
        entityType: props.entityType,
        destination: props.destination,
        toolName: props.toolName,
        columnKind: props.columnKind,
      };
    case 'csv_download':
      return {
        ...base,
        scope: props.scope,
        toolName: props.toolName,
        rowCount: props.rowCount,
      };
    case 'stop':
      return {
        ...base,
        turnIndex: props.turnIndex,
      };
    case 'attach_interest':
      return {
        ...base,
        method: props.method,
        fileKind: props.fileKind,
        fileCount: props.fileCount,
      };
    case 'answer_read':
      return {
        ...base,
        source: props.source,
        placement: props.placement,
        cached: props.cached,
        trigger: props.trigger,
        startedBy: props.startedBy,
        queryShape: props.queryShape,
        answerMs: props.answerMs === undefined ? undefined : Math.round(props.answerMs),
        visibleMs: Math.round(props.visibleMs),
      };
    default:
      return base;
  }
};

/** End the current session with a summary rollup, then optionally reset. */
export const endAiSearchSession = (reason: 'new_chat' | 'minimize', resetAfter = false) => {
  if (!session || session.interactionIndex === 0) {
    if (resetAfter) {
      resetAiSearchSession();
    }
    return;
  }
  const current = session;
  current.interactionIndex += 1;
  analyticsLogger().logEvent(
    'AI_SEARCH_INTERACTION',
    buildEventPayload(current, {action: 'session_summary', reason})
  );
  if (resetAfter) {
    session = null;
  }
};

/** Amplitude event for AI search drawer / chat interactions. */
export const logAiSearchInteraction = (props: AiSearchInteractionProps) => {
  // A search pane answer isn't part of a drawer session: it leaves its counts alone.
  const current =
    props.action === 'answer_read' ? ensureSession() : bumpSessionForAction(props.action);
  analyticsLogger().logEvent('AI_SEARCH_INTERACTION', buildEventPayload(current, props));
};

/** Log drawer first-expand impression once per page load. */
let drawerViewed = false;
export const logAiSearchDrawerViewed = () => {
  if (drawerViewed) {
    return;
  }
  drawerViewed = true;
  analyticsLogger().logEvent('AI_SEARCH_DRAWER_VIEWED', {surface: 'ai_search'});
};

/** Test-only reset of module state. */
export const _resetAiSearchAnalyticsForTests = () => {
  session = null;
  drawerViewed = false;
};
